/**
 * 用户管理模块 - 完整的CRUD功能
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Table, Button, Input, Select, Space, Modal, message, Form, Switch, Tag, Row, Col } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined, ReloadOutlined, UserOutlined, KeyOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { userService } from '../../../../../services/userService';
import { roleService } from '../../../../../services/roleService';
import { permissionService } from '../../../../../services/permissionService';
import { PAGINATION_CONFIG } from '../../components/PaginationConfig';
import QueryArea, { QueryField } from '../../components/QueryArea';
import { useAuth } from '../../../../../hooks/useAuth';

const { Password } = Input;

interface User {
  id: number;
  username: string;
  nickname: string;
  email: string;
  phone: string;
  roles: string[];
  roleIds: number[]; // 添加角色ID数组
  status: number;
  lastLoginTime?: string;
  createTime: string;
  password?: string; // 密码字段（可选，编辑时不使用）
}

interface Role {
  id: number;
  roleName: string;
  roleKey: string;
  status: number;
}

const UserManagement: React.FC = () => {
  const { user: currentUser } = useAuth(); // 获取当前登录用户
  const [dataSource, setDataSource] = useState<User[]>([]);
  const [roleList, setRoleList] = useState<Role[]>([]); // 添加角色列表状态
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [current, setCurrent] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchParams, setSearchParams] = useState({
    keyword: '',
    role: '',
    status: '',
  });
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [form] = Form.useForm();
  const [resetPasswordForm] = Form.useForm();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState<User | null>(null);
  const [resetPasswordModalVisible, setResetPasswordModalVisible] = useState(false);
  const [resettingUserId, setResettingUserId] = useState<number | null>(null);

  // 查询字段配置
  const queryFields: QueryField[] = [
    {
      key: 'keyword',
      label: '用户名',
      type: 'input',
      placeholder: '搜索用户名或昵称...',
      width: 200,
    },
    {
      key: 'role',
      label: '角色',
      type: 'select',
      placeholder: '选择角色',
      width: 150,
      options: [
        { label: '超级管理员', value: '超级管理员' },
        { label: '管理员', value: '管理员' },
        { label: '编辑员', value: '编辑员' },
        { label: '审核员', value: '审核员' },
      ],
    },
    {
      key: 'status',
      label: '状态',
      type: 'select',
      placeholder: '选择状态',
      width: 120,
      options: [
        { label: '正常', value: '1' },
        { label: '禁用', value: '0' },
      ],
    },
  ];

  // 表格列定义 - 优化后只显示必要字段，调整列宽消除滚动条
  const columns: ColumnsType<User> = [
    {
      title: '用户名',
      dataIndex: 'username',
      key: 'username',
      width: 90,
      ellipsis: true,
    },
    {
      title: '昵称',
      dataIndex: 'nickname',
      key: 'nickname',
      width: 90,
      ellipsis: true,
    },
    {
      title: '角色',
      dataIndex: 'roles',
      key: 'roles',
      width: 110,
      ellipsis: true,
      render: (roles: string[]) => (
        <Space wrap>
          {roles?.map(role => (
            <Tag key={role} color="blue" style={{ fontSize: '12px' }}>{role}</Tag>
          ))}
        </Space>
      ),
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 70,
      align: 'center',
      render: (value) => (
        <span className={`status-tag ${value === 1 ? 'active' : 'inactive'}`}>
          {value === 1 ? '正常' : '禁用'}
        </span>
      ),
    },
    {
      title: '最后登录时间',
      dataIndex: 'lastLoginTime',
      key: 'lastLoginTime',
      width: 150,
      ellipsis: true,
      render: (value) => value ? new Date(value).toLocaleString('zh-CN') : '从未登录',
    },
    {
      title: '操作',
      key: 'action',
      width: 200,
      align: 'center',
      fixed: 'right',
      render: (_, record) => {
        // 判断是否是管理员用户
        const isAdminUser = record.username === 'admin';
        // 判断当前登录用户是否是管理员
        const isCurrentUserAdmin = currentUser?.username === 'admin';
        // 判断是否是重置自己的密码
        const isSelfReset = isAdminUser && currentUser?.id === record.id;
        // 编辑按钮：管理员用户不能编辑
        const canEdit = !isAdminUser;
        // 重置密码按钮：管理员用户只能自己重置自己的密码
        const canResetPassword = !isAdminUser || (isCurrentUserAdmin && isSelfReset);
        
        return (
          <Space size="small" wrap>
          <Button 
            type="primary" 
            size="small" 
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
              disabled={!canEdit}
          >
            编辑
          </Button>
            <Button 
              size="small" 
              icon={<KeyOutlined />}
              onClick={() => handleResetPassword(record)}
              disabled={!canResetPassword}
            >
              重置密码
            </Button>
          <Button 
            danger 
            size="small" 
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record.id)}
            disabled={record.username === 'admin'}
          >
            删除
          </Button>
        </Space>
        );
      },
    },
  ];

  // 加载角色列表
  const loadRoleList = async () => {
    try {
      const response = await roleService.getList({ status: 1, size: 1000 }); // 获取所有正常状态的角色
      if (response.code === 200) {
        setRoleList(response.data.records || []);
      }
    } catch (error) {
      console.error('加载角色列表失败:', error);
    }
  };

  // 数据加载
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        current,
        size: pageSize,
        ...searchParams,
      };

      const response = await userService.getList(params);
      if (response.code === 200) {
        setDataSource(response.data.records || []);
        setTotal(response.data.total || 0);
      } else {
        message.error(response.message || '加载数据失败');
      }
    } catch (error) {
      console.error('加载数据失败:', error);
      message.error('加载数据失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  }, [current, pageSize, searchParams]);

  useEffect(() => {
    loadData();
  }, [current, pageSize, searchParams]);

  useEffect(() => {
    loadRoleList(); // 加载角色列表
  }, []);

  // 搜索
  const handleSearch = () => {
    setCurrent(1);
    loadData();
  };

  // 重置
  const handleReset = () => {
    setSearchParams({ keyword: '', role: '', status: '' });
    setCurrent(1);
    setTimeout(loadData, 0);
  };

  // 处理查询参数变化
  const handleSearchParamsChange = useCallback((values: Record<string, any>) => {
    setSearchParams({
      keyword: values.keyword || '',
      role: values.role || '',
      status: values.status || '',
    });
  }, []);

  // 新增
  const handleAdd = () => {
    setEditingRecord(null);
    // 完全重置表单，清除所有字段值
    form.resetFields();
    // 确保所有字段都被清空，包括密码字段
    form.setFieldsValue({
      username: '',
      nickname: '',
      email: '',
      phone: '',
      password: '',
      status: 1,
      roleIds: [],
    });
    setIsModalVisible(true);
  };

  // 编辑
  const handleEdit = async (record: User) => {
    // 检查是否是管理员用户，如果是则不允许编辑
    if (record.username === 'admin') {
      message.error('不能编辑超级管理员信息');
      return;
    }
    
    setEditingRecord(record);
    
    // 获取用户的角色ID列表
    let roleIds: number[] = [];
    try {
      const roleResponse = await permissionService.getUserRoleIds(record.id);
      
      if (roleResponse.code === 200 && roleResponse.data) {
        roleIds = roleResponse.data;
      }
    } catch (error) {
      console.error('获取用户角色ID失败:', error);
    }
    
    // 编辑时不设置密码字段，避免显示加密后的密码
    // 从记录中排除密码字段和其他不需要的字段
    const { password, roles, ...userDataWithoutPassword } = record as any;
    form.setFieldsValue({
      ...userDataWithoutPassword,
      status: record.status === 1, // 将数字状态转换为布尔值
      roleIds: roleIds, // 设置角色ID列表
    });
    setIsModalVisible(true);
  };

  // 重置密码
  const handleResetPassword = (record: User) => {
    // 如果是管理员用户
    if (record.username === 'admin') {
      // 检查当前登录用户是否是管理员自己
      const isCurrentUserAdmin = currentUser?.username === 'admin';
      // 检查是否是重置自己的密码
      const isSelf = currentUser?.id === record.id;
      
      if (!isCurrentUserAdmin || !isSelf) {
        message.error('不能重置超级管理员密码');
        return;
      }
      // 如果是管理员自己重置自己的密码，允许
    }
    
    setResettingUserId(record.id);
    resetPasswordForm.resetFields();
    setResetPasswordModalVisible(true);
  };

  // 确认重置密码
  const handleConfirmResetPassword = async () => {
    try {
      const values = await resetPasswordForm.validateFields();
      if (!resettingUserId) {
        message.error('用户ID不存在');
        return;
      }
      
      setLoading(true);
      const response = await userService.resetPassword(resettingUserId, values.newPassword);
      
      if (response.code === 200) {
        message.success('密码重置成功');
        setResetPasswordModalVisible(false);
        resetPasswordForm.resetFields();
        setResettingUserId(null);
      } else {
        message.error(response.message || '密码重置失败');
      }
    } catch (error) {
      console.error('重置密码失败:', error);
      message.error('重置密码失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  // 删除
  const handleDelete = (id: number) => {
    const user = dataSource.find(u => u.id === id);
    if (user?.username === 'admin') {
      message.error('不能删除超级管理员账户');
      return;
    }

    Modal.confirm({
      title: '确认删除',
      content: '确定要删除这个用户吗？删除后无法恢复。',
      okText: '确定',
      cancelText: '取消',
      okType: 'danger',
      onOk: async () => {
        setLoading(true);
        try {
          const response = await userService.delete(id);
          if (response.code === 200) {
            message.success('删除成功');
            loadData();
          } else {
            message.error(response.message || '删除失败');
          }
        } catch (error) {
          console.error('删除失败:', error);
          message.error('删除失败，请稍后重试');
        } finally {
          setLoading(false);
        }
      },
    });
  };

  // 批量删除
  const handleBatchDelete = () => {
    if (selectedRowKeys.length === 0) {
      message.warning('请先选择要删除的数据');
      return;
    }

    const hasAdmin = selectedRowKeys.some(id => {
      const user = dataSource.find(u => u.id === id);
      return user?.username === 'admin';
    });

    if (hasAdmin) {
      message.error('选中的用户中包含超级管理员，不能删除');
      return;
    }
    
    Modal.confirm({
      title: '确认批量删除',
      content: `确定要删除选中的 ${selectedRowKeys.length} 个用户吗？删除后无法恢复。`,
      okText: '确定',
      cancelText: '取消',
      okType: 'danger',
      onOk: async () => {
        setLoading(true);
        try {
          const response = await userService.batchDelete(selectedRowKeys as number[]);
          if (response.code === 200) {
            message.success('批量删除成功');
            setSelectedRowKeys([]);
            loadData();
          } else {
            message.error(response.message || '批量删除失败');
          }
        } catch (error) {
          console.error('批量删除失败:', error);
          message.error('批量删除失败，请稍后重试');
        } finally {
          setLoading(false);
        }
      },
    });
  };

  // 表单提交
  const handleFormSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);
      
      // 处理状态字段：将boolean转换为number
      const processedValues: any = {
        ...values,
        status: values.status ? 1 : 0
      };
      
      let response;
      if (editingRecord) {
        // 编辑用户：不传递密码字段，保持原密码不变
        const { password, roleIds, ...updateData } = processedValues;
        response = await userService.update({ ...updateData, id: editingRecord.id });
        
        // 更新用户角色：只在角色确实被修改时才更新
        // 获取用户当前的角色ID列表，用于比较是否有变化
        let currentRoleIds: number[] = [];
        try {
          const roleResponse = await permissionService.getUserRoleIds(editingRecord.id);
          if (roleResponse.code === 200 && roleResponse.data) {
            currentRoleIds = roleResponse.data;
          }
        } catch (error) {
          console.error('获取用户角色ID失败:', error);
        }
        
        // 获取表单中的角色ID列表（如果存在）
        const formRoleIds = roleIds || [];
        
        // 比较角色ID列表是否有变化（排序后比较，确保顺序不影响比较）
        const currentRoleIdsSorted = [...currentRoleIds].sort((a, b) => a - b).join(',');
        const formRoleIdsSorted = [...formRoleIds].sort((a, b) => a - b).join(',');
        
        // 只有当角色有变化时才更新角色分配
        // 这样即使用户只修改了状态（禁用/启用），也不会清空角色授权
        if (currentRoleIdsSorted !== formRoleIdsSorted) {
          const roleResponse = await permissionService.assignUserRoles(editingRecord.id, formRoleIds);
          if (roleResponse.code !== 200) {
            message.warning('用户信息更新成功，但角色分配失败：' + (roleResponse.message || '未知错误'));
          }
        }
        // 如果角色没有变化，不更新角色分配，保持原有角色授权
      } else {
        // 创建用户：必须包含密码字段
        response = await userService.create(processedValues);
        
        // 分配用户角色
        if (response.code === 200 && response.data && response.data.id && values.roleIds && values.roleIds.length > 0) {
          const roleResponse = await permissionService.assignUserRoles(response.data.id, values.roleIds);
          if (roleResponse.code !== 200) {
            message.warning('用户创建成功，但角色分配失败：' + (roleResponse.message || '未知错误'));
          }
        }
      }

      if (response.code === 200) {
        message.success(editingRecord ? '编辑成功' : '新增成功');
        setIsModalVisible(false);
        // 延迟重置表单，确保模态框关闭动画完成
        setTimeout(() => {
          form.resetFields();
          setEditingRecord(null);
        }, 300);
        loadData();
      } else {
        message.error(response.message || '提交失败');
      }
    } catch (error) {
      console.error('提交失败:', error);
      message.error('提交失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="user-management">
      {/* 统一查询区域 */}
      <QueryArea
        fields={queryFields}
        values={searchParams}
        onValuesChange={handleSearchParamsChange}
        onAdd={handleAdd}
        onBatchDelete={handleBatchDelete}
        batchDeleteDisabled={selectedRowKeys.length === 0}
        selectedCount={selectedRowKeys.length}
        addButtonText="新增用户"
        batchDeleteButtonText="批量删除"
      />

      {/* 数据表格 */}
      <div className="table-card">
        <Table
          columns={columns}
          dataSource={dataSource}
          loading={loading}
          rowKey="id"
          rowSelection={{
            selectedRowKeys,
            onChange: setSelectedRowKeys,
            getCheckboxProps: (record) => ({
              disabled: record.username === 'admin',
            }),
          }}
          pagination={{
            current,
            pageSize,
            total,
            ...PAGINATION_CONFIG.simple,
            onChange: (page, size) => {
              setCurrent(page);
              setPageSize(size);
            },
          }}
        />
      </div>

      {/* 新增/编辑模态框 */}
      <Modal
        title={editingRecord ? '编辑用户' : '新增用户'}
        open={isModalVisible}
        onOk={handleFormSubmit}
        onCancel={() => {
          setIsModalVisible(false);
          setTimeout(() => {
            form.resetFields();
            setEditingRecord(null);
          }, 300);
        }}
        width={600}
        confirmLoading={loading}
        okText="确定"
        cancelText="取消"
      >
        <Form
          form={form}
          layout="horizontal"
          labelCol={{ span: 4 }}
          wrapperCol={{ span: 20 }}
          autoComplete="off"
          initialValues={{
            status: 1,
            roleIds: [],
          }}
        >
          {/* 第一排：用户名和昵称 */}
          <Row gutter={16}>
            <Col span={12}>
            <Form.Item
              name="username"
                label="用户名:"
              rules={[
                { required: true, message: '请输入用户名' },
                { min: 3, message: '用户名至少3个字符' },
                { max: 20, message: '用户名最多20个字符' }
              ]}
                labelCol={{ span: 8 }}
                wrapperCol={{ span: 16 }}
                labelAlign="left"
              >
                <Input 
                  placeholder="请输入用户名" 
                  disabled={!!editingRecord}
                  autoComplete="off"
                />
            </Form.Item>
            </Col>
            <Col span={12}>
            <Form.Item
              name="nickname"
                label="昵称:"
              rules={[{ required: true, message: '请输入昵称' }]}
                labelCol={{ span: 6 }}
                wrapperCol={{ span: 18 }}
                labelAlign="left"
            >
                <Input 
                  placeholder="请输入昵称" 
                  autoComplete="off"
                />
            </Form.Item>
            </Col>
          </Row>

          {/* 第二排：邮箱和手机号 */}
          <Row gutter={16}>
            <Col span={12}>
            <Form.Item
              name="email"
                label="邮箱:"
                rules={[
                  {
                    // 如果字段为空，跳过验证；如果填写了，验证邮箱格式
                    validator: (_, value) => {
                      if (!value || value.trim() === '') {
                        return Promise.resolve();
                      }
                      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                      if (emailRegex.test(value)) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error('请输入有效的邮箱地址'));
                    }
                  }
                ]}
                labelCol={{ span: 8 }}
                wrapperCol={{ span: 16 }}
                labelAlign="left"
              >
                <Input 
                  placeholder="请输入邮箱地址（可选）" 
                  autoComplete="off"
                  type="email"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="phone"
                label="手机号:"
              rules={[
                  {
                    // 如果字段为空，跳过验证；如果填写了，验证手机号格式
                    validator: (_, value) => {
                      if (!value || value.trim() === '') {
                        return Promise.resolve();
                      }
                      const phoneRegex = /^1[3-9]\d{9}$/;
                      if (phoneRegex.test(value)) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error('请输入有效的手机号'));
                    }
                  }
                ]}
                labelCol={{ span: 6 }}
                wrapperCol={{ span: 18 }}
                labelAlign="left"
              >
                <Input 
                  placeholder="请输入手机号（可选）" 
                  autoComplete="off"
                />
            </Form.Item>
            </Col>
          </Row>

          {/* 第三排：密码（只在新增用户时显示） */}
          {!editingRecord && (
            <Form.Item
              name="password"
              label="密码:"
              rules={[
                { required: true, message: '请输入密码' },
                { min: 6, message: '密码至少6个字符' },
                { max: 20, message: '密码最多20个字符' }
              ]}
              labelAlign="left"
              extra="新增用户时必须设置密码"
            >
              <Password 
                placeholder="请输入密码" 
                autoComplete="new-password"
              />
            </Form.Item>
          )}

          {/* 第四排：角色 */}
          <Form.Item
            name="roleIds"
            label="角色:"
            rules={[{ required: true, message: '请选择角色' }]}
            labelAlign="left"
          >
            <Select mode="multiple" placeholder="请选择角色">
              {roleList.map(role => (
                <Select.Option key={role.id} value={role.id}>
                  {role.roleName} ({role.roleKey})
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          {/* 第五排：状态 */}
          <Form.Item
            name="status"
            label="状态:"
            valuePropName="checked"
            labelAlign="left"
          >
            <Switch checkedChildren="正常" unCheckedChildren="禁用" />
          </Form.Item>
        </Form>
      </Modal>

      {/* 重置密码模态框 */}
      <Modal
        title="重置用户密码"
        open={resetPasswordModalVisible}
        onOk={handleConfirmResetPassword}
        onCancel={() => {
          setResetPasswordModalVisible(false);
          resetPasswordForm.resetFields();
          setResettingUserId(null);
        }}
        width={500}
        confirmLoading={loading}
        okText="确定"
        cancelText="取消"
      >
        <Form
          form={resetPasswordForm}
          layout="vertical"
          autoComplete="off"
        >
          <Form.Item
            name="newPassword"
            label="新密码"
            rules={[
              { required: true, message: '请输入新密码' },
              { min: 6, message: '密码至少6个字符' },
              { max: 20, message: '密码最多20个字符' }
            ]}
          >
            <Password placeholder="请输入新密码" autoComplete="new-password" />
          </Form.Item>
          <Form.Item
            name="confirmPassword"
            label="确认密码"
            dependencies={['newPassword']}
            rules={[
              { required: true, message: '请确认新密码' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('newPassword') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('两次输入的密码不一致'));
                },
              }),
            ]}
          >
            <Password placeholder="请再次输入新密码" autoComplete="new-password" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default UserManagement;












